<template>
  <VDialog
    v-model="isShown"
    class="easy-modal"
    :class="props.modalClass"
    :persistent="true"
    :no-click-animation="true"
    :fullscreen="isSmallScreenSize"
    :width="props.width"
    :retain-focus="false"
    :min-width="props.minWidth"
    :z-index="props.zIndex"
    :scrim="props.showOverlay"
    max-width="100vw"
    scroll-strategy="none"
    @click="handleMaskClick"
  >
    <div
      ref="modalContentRef"
      class="easy-modal__content"
      :class="props.contentClass"
    >
      <div
        v-if="props.errorDescription"
        class="easy-modal__content--alert"
      >
        <CloseBtn @close="isShown = false" />
        <EasyAlert :text="props.errorDescription" />
      </div>

      <template v-else>
        <div
          v-if="!props.hideHeader"
          class="easy-modal__content--header"
        >
          <EasySkeletonLoader
            v-if="props.loading"
            data-cy="skeleton__easy-modal"
            class="ml-4 mr-20 h-20"
            structure="heading,divider"
          />

          <div
            v-else-if="props.title"
            class="easy-modal__content--header-title"
          >
            {{ title }}
          </div>
          <slot
            v-else
            name="header"
          />

          <CloseBtn
            data-cy="button__close--easy-modal"
            @close="isShown = false"
          />
        </div>

        <div class="easy-modal__content--body">
          <EasySkeletonLoader
            v-if="props.loading"
            data-cy="skeleton__easy-modal"
            structure="card,card,text,divider"
            class="w-full mb-4 mx-4"
          />
          <slot />
          <slot
            v-if="!props.hideSidebar"
            name="sidebar"
          />
        </div>

        <div
          v-if="!props.hideFooter"
          class="easy-modal__content--footer"
        >
          <EasySkeletonLoader
            v-if="props.loading"
            data-cy="skeleton__easy-modal"
            structure="actions"
            class="mr-4 mb-2"
          />
          <slot
            v-else
            name="footer"
          />
        </div>
      </template>
    </div>
  </VDialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, WritableComputedRef } from "vue";
import { useDeviceCheck } from "@/src/shared/composables/deviceCheck";
import { useFocusTrap } from "@/src/design_system";
import CloseBtn from "./EasyModalCloseBtn.vue";
import EasyAlert from "./EasyAlert.vue";
import EasySkeletonLoader from "./EasySkeletonLoader.vue";

interface Props {
  modelValue: boolean;
  closeOnMaskClick?: boolean;
  loading?: boolean;
  modalClass?: string | Array<string>;
  contentClass?: string | Array<string>;
  errorDescription?: string;
  width?: number | string;
  title?: string;
  hideFooter?: boolean;
  hideHeader?: boolean;
  hideSidebar?: boolean;
  persistent?: boolean;
  minWidth?: string | number;
  zIndex?: string | number;
  showOverlay?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  closeOnMaskClick: true,
  modalClass: "",
  contentClass: "",
  errorDescription: "",
  width: "",
  title: "",
  hideFooter: false,
  hideHeader: false,
  hideSidebar: false,
  showOverlay: true,
  minWidth: "",
  zIndex: "unset",
});

interface Emits {
  (e: "update:modelValue", isShown: boolean): void;
  (e: "close"): void;
  (e: "open"): void;
}

const emit = defineEmits<Emits>();

const isShown: WritableComputedRef<boolean> = computed({
  get: () => props.modelValue,
  set: (isShown: boolean) => {
    emit("update:modelValue", isShown);
    if (!isShown) emit("close");
    if (isShown) emit("open");
  },
});

const handleMaskClick = (e: MouseEvent): void => {
  if (!props.closeOnMaskClick) return;
  const target = e.target as Element;
  if (target.className !== "v-overlay__scrim") return;

  isShown.value = false;
};

const { isSmallScreenSize } = useDeviceCheck();

const modalContentRef = ref<HTMLElement | null>(null);

useFocusTrap({ dialogRef: modalContentRef, isModal: ref(true) });

onMounted(() => {
  window.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleEscKey);
});

const handleEscKey = (e: KeyboardEvent) => {
  if (e.key === "Escape") isShown.value = false;
};
</script>
